"use client";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { MapPin, Phone } from "lucide-react";
const hotels = [{
  id: 1,
  name: "Terre Brune Hotel Kfardebian",
  location: "Kfardebian - Faqra Road, Kfardebian, Lebanon",
  phone: "+961",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1508139/img/image_gncwt7yy.png",
  discountCode: "Discount Code: VanessaBrian",
  note: "Call +961 to book, mention \"VanessaBrian\" discount code.",
  rooms: [{
    type: "Deluxe Room",
    price: "$"
  }, {
    type: "Executive Room",
    price: "$"
  }, {
    type: "Majestic Room",
    price: "$"
  }, {
    type: "Captain Suite",
    price: "$"
  }, {
    type: "Terrebrune Suite",
    price: "$"
  }]
}, {
  id: 2,
  name: "InterContinental Mzaar (Mountain Resort & Spa) by IHG",
  location: "Kfardebian, Lebanon",
  phone: "",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1508139/img/image_h0ltzaoq.jpg",
  discountCode: "Discount Code: VanessaBrian",
  note: "Use the code \"VanessaBrian\" when calling or booking.",
  rooms: []
}, {
  id: 3,
  name: "Faqra Club Kfardebian",
  location: "Faqra, Mount Lebanon Governorate, Lebanon",
  phone: "",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1508139/img/image_zxyaotay.png",
  discountCode: "Discount Code: VanessaBrian",
  note: "Use the code \"VanessaBrian\" when calling or booking.",
  rooms: []
}];
function HotelCard({
  hotel,
  index
}: {
  hotel: typeof hotels[0];
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.15,
    once: true
  });
  const getHotelUrl = (idx: number): string => {
    if (idx === 0) return "http://terre-brune-hotel.beirut-hotel.net/en/";
    if (idx === 1) return "https://www.ihg.com/intercontinental/hotels/us/en/mzaar/mzrha/hoteldetail";
    return "https://www.faqraclub.com/Hotel/hotel-and-pool";
  };
  return <div ref={ref} data-index={index} className="transition-all duration-[var(--duration-fast)] ease-[cubic-bezier(0.4,0,0.2,1)]" style={{
    opacity: isVisible ? 1 : 0,
    transform: isVisible ? "translateY(0)" : "translateY(10px)"
  }}>
      <Card className="overflow-hidden hover:shadow-medium hover:-translate-y-1 transition-all duration-[var(--duration-medium)] ease-[var(--ease-out-back)] bg-accent border border-muted">
        <div className="relative">
          <img src={hotel.image} alt={hotel.name} className="w-full h-56 object-cover" />
          <Badge variant="secondary" className="absolute top-4 right-4 bg-primary-dark text-white shadow-medium">
            {hotel.discountCode}
          </Badge>
        </div>

        <CardHeader>
          <CardTitle className="text-xl text-foreground font-display">
            {hotel.name}
          </CardTitle>
          <CardDescription className="flex items-start gap-1.5 text-muted-foreground">
            <MapPin className="size-4 mt-0.5 shrink-0" />
            <span>{hotel.location}</span>
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-4">
          {hotel.phone && <div className="flex items-center gap-2 text-sm text-foreground">
              <Phone className="size-4" />
              <span>{hotel.phone}</span>
            </div>}

          {hotel.rooms.length > 0 && <div className="space-y-2">
              <p className="text-sm font-semibold text-foreground">Room Options:</p>
              <ul className="space-y-1">
                {hotel.rooms.map((room, i) => <li key={i} data-index={i} className="text-sm text-muted-foreground flex justify-between">
                    <span>{room.type}</span>
                    <span className="font-medium">{room.price}</span>
                  </li>)}
              </ul>
            </div>}

          <p className="text-sm text-muted-foreground italic">{hotel.note}</p>
        </CardContent>

        <CardFooter>
          <Button asChild variant="default" className="w-full hover:scale-[1.03] transition-transform duration-[var(--duration-medium)]">
            <Link to={getHotelUrl(index)}>View Details</Link>
          </Button>
        </CardFooter>
      </Card>
    </div>;
}
export default function Hotel() {
  return <section className="py-16 px-4 bg-light-background relative overflow-hidden !bg-[url(https://vanessabrian.com/wp-content/uploads/2026/02/background-3.jpg)]">
      {}
      <div className="absolute right-0 top-1/4 w-96 h-96 opacity-15 pointer-events-none hidden lg:block z-0">
        <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1508139/img/image_opg9zpq8.png" alt="" className="w-full h-full object-contain rotate-12 transform transition-transform duration-[var(--duration-slow)]" aria-hidden="true" />
      </div>

      <div className="max-w-4xl mx-auto space-y-12 relative z-10">
        <div className="text-center space-y-3">
          <h2 className="text-4xl font-display text-foreground">
            Hotel Accommodations
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            For guests who would like to stay close to the venue and avoid driving at night,
            we've listed a few hotel options below. Vanessa and Brian, along with their wedding party, 
            will be staying at Terre Brune Hotel.
          </p>
          <p className="text-base text-foreground font-medium">
            Please use the discount code "VanessaBrian" when calling to make your reservation.
          </p>
        </div>

        <div className="grid gap-8">
          {hotels.map((hotel, i) => <HotelCard key={hotel.id} hotel={hotel} index={i} />)}
        </div>
      </div>
    </section>;
}